import {createSlice} from '@reduxjs/toolkit'
// import { isValidElement } from 'react'
import {
    APPLICATION_STATUS_PENDING,
    APPLICATION_STATUS_PENDING_AGENCY,
    APPLICATION_STATUS_UNDER_REVIEW,
    APPLICATION_STATUS_UNREAD,
    APPLICATION_STATUS_OPENED,
    ERROR_CODE, 
    FETCH_APPLICATIONS_URL,
    FETCH_ARCHIVE_APPLICATIONS_URL,
    LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION, 
    LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION, 
    LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION, 
    LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION, 
    REQUEST_STATUS,
    SAVE_APPLICATION_STATUS_URL,
    FETCH_ADMIN_QUEUE_URL_LOCAL_TESTING,
    LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION,
    LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION,
    SAVE_LICENCE_STATUS_URL,
    LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_RENEWAL,
    LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_RENEWAL,
    LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL,
    LICENCE_SUB_TYPE_DEBT_MANAGEMENT_RENEWAL,
    LICENCE_SUB_TYPE_ALL,
    LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION,
    LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION,
    LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION,
    FETCH_ADMIN_QUEUE
} from "../../app/constants"
import { isAgencyTypeApplication, isIndividualTypeApplication } from './applicationsUtils'

const bltHomeSlice = createSlice({
    name : "bltHome",
    initialState : {
        queue : null,
        sortingParameters : {
            key : "dateReceived",
            sortBy : "SUBMITTED_DATE",
            isAscending : false
        }
    },
    reducers : {
        setBusinessLicenceQueue(state, action){ 
            state.queue = action.payload.processedData
            state.paginationData = action.payload.paginationData
        },
        updateQueueItem(state, action) { 
            const updatedQueueItem = action.payload
            if(updatedQueueItem
                && state.queue 
                && Array.isArray(state.queue)) 
            { 
                const queueIndex = state.queue.findIndex(q => q.id === updatedQueueItem.id)
                if(queueIndex >= 0) { 
                    state.queue.splice(queueIndex,1,updatedQueueItem)
                }
            }
        }, 
        setFetchApplicationRequestStatus(state, action) { 
            state.fetchAppRequestStatus = action.payload
        },
        setFetchQueueRequestStatus(state, action) { 
            state.fetchQueueRequestStatus = action.payload
        },
        resetQueue(state, action) { 
            state.queue = null;
            state.paginationData = null;
        },
        setSortingParameters(state, action) {
            state.sortingParameters = action.payload
        }
    }
})

export const { 
    setBusinessLicenceQueue,
    updateQueueItem,
    setFetchApplicationRequestStatus,
    setFetchQueueRequestStatus,
    resetQueue,
    setSortingParameters
} = bltHomeSlice.actions

/*
   The queue will have applications as per below logic : 
    - main agency application (primary application)
        - with businessApplicationId set to null or 0 or undefined
        - there should not be any entry in the additionalBranches
    - additional branch application
        - with businessApplicationId set to id of the primary agency application
        - it will only have additionalBranches entry.
        - (no other data should be considered from this application)
        - This data should be merged in the primary application.
    - individual agent application
        - with businessApplcationId set to the id of the primary application
        - this entry needs to be shown separately in the queue.
 */
function processQueueItemResponse(queue) { 
    const processedQ = []

    // put applications in its respective objects with its main id as the key and queue item object as value
    queue.forEach(q => {
        if(isAgencyTypeApplication(q)) 
        {
            // hack for avoiding entried with name "null null"
            // such entries are for branch application where server
            // tries to put name but put "null null" instead
            if(1 || (!q.businessApplicationId && 
                q.contactName !== "null null")) { 
                processedQ.push(q) 
            }
        } else if(isIndividualTypeApplication(q)) 
        { 
            processedQ.push(q)
        }
    })

    return processedQ
}

/*
 * Below function can turn into a blocker call for a long time if the list of applicationIds is long.
 */
export const fetchMultipleApplicationsWithId = (applicationIds) => async (dispatch, getState) => {
    if(getState().bltComponents.bltHome.fetchAppRequestStatus === REQUEST_STATUS.IN_PROGRESS) { 
        return Promise.reject(ERROR_CODE.REQUEST_OUTSTANDING)
    }

    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.IN_PROGRESS))

    let requestUrl = FETCH_APPLICATIONS_URL

    if(window.location.hostname === "localhost") {
        requestUrl = requestUrl + "WithId"
    }

    let responseApplications = []

    for(let i = 0; i < applicationIds.length; i++) { 

        const resp = await fetch(requestUrl + "?id=" + applicationIds[i])

        if(resp.status === 401 || resp.status === 403) {
            dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.ERROR)) 
            break
        }

        const respJson = await resp.json()

        if(respJson.businessLicenceList 
            && Array.isArray(respJson.businessLicenceList)
            && respJson.businessLicenceList.length > 0) 
        { 
            const applicationResp = respJson.businessLicenceList.find(d => d.id === applicationIds[i])
            
            if(applicationResp)
                responseApplications = [...responseApplications, applicationResp]
        }
    }

    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.SUCCESS))
    return Promise.resolve(responseApplications)
}

/*
 * The application for collection agency will have a different application for additional
 * branches. We need to check if the returned application is for collection or debt management
 * agency and if so, query for additional branch application as well.
 */
export const fetchApplicationWithId = (applicationId, ignoreInProgressStatus, isArchiveApplication) => async (dispatch, getState) => {
    if(!ignoreInProgressStatus && getState().bltComponents.bltHome.fetchAppRequestStatus === REQUEST_STATUS.IN_PROGRESS) { 
        return Promise.reject(ERROR_CODE.REQUEST_OUTSTANDING)
    }

    if(!ignoreInProgressStatus) {
        dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.IN_PROGRESS))
    }

    let requestUrl = FETCH_APPLICATIONS_URL

    if(isArchiveApplication) {
        requestUrl = FETCH_ARCHIVE_APPLICATIONS_URL
    }

    if(window.location.hostname === "localhost") {
        requestUrl = requestUrl + "WithId"
    }

    const resp = await fetch(requestUrl + "?id=" + applicationId)

    if(resp.status === 401 || resp.status === 403) {
        if(!ignoreInProgressStatus) {
            dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.ERROR))} 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(!ignoreInProgressStatus) {
        dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.SUCCESS))
    }

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0) 
    { 
        return respJson.businessLicenceList.find(d => d.id === applicationId)
    }

    return {}
}

export const fetchIndividualApplicationsForBusinessApplicationId = (businessApplicationId, licencingSubTypes, forceFetch = false) => async (dispatch, getState) => {
    if(!forceFetch && getState().bltComponents.bltHome.fetchAppRequestStatus === REQUEST_STATUS.IN_PROGRESS) { 
        return Promise.reject(ERROR_CODE.REQUEST_OUTSTANDING)
    }

    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.IN_PROGRESS))

    const resp = await fetch(`${FETCH_APPLICATIONS_URL}?businessApplicationId=${businessApplicationId}`)

    if(resp.status === 401 || resp.status === 403) {
        dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.ERROR)) 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()
    
    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.SUCCESS))

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0)
    {
        return respJson.businessLicenceList.filter(application => 
            ((licencingSubTypes.findIndex(subType => subType === application.licencingSubType) !== -1) 
                && (application.businessApplicationId === businessApplicationId)
                && (application.applicationStatus === APPLICATION_STATUS_UNREAD || 
                    application.applicationStatus === APPLICATION_STATUS_UNDER_REVIEW ||
                    application.applicationStatus === APPLICATION_STATUS_OPENED || 
                    application.applicationStatus === APPLICATION_STATUS_PENDING ||
                    application.applicationStatus === APPLICATION_STATUS_PENDING_AGENCY
                    )))
    }

    return []
}

export const fetchBranchApplicationsForPrimaryApplicationId = (businessApplicationId, licencingSubType, forceFetch = false, isArchiveApplication = false) => async (dispatch, getState) => {
    if(!forceFetch && getState().bltComponents.bltHome.fetchAppRequestStatus === REQUEST_STATUS.IN_PROGRESS) { 
        return Promise.reject(ERROR_CODE.REQUEST_OUTSTANDING)
    }

    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.IN_PROGRESS))

    let requestUrl = FETCH_APPLICATIONS_URL

    if(isArchiveApplication) {
        requestUrl = FETCH_ARCHIVE_APPLICATIONS_URL
    }

    if(window.location.hostname === "localhost") {
        // requestUrl = FETCH_APPLICATIONS_URL + "WithId"
        requestUrl = "/api/branches"
    }
    const resp = await fetch(`${requestUrl}?branchId=${businessApplicationId}&licenceSubType=${licencingSubType}`)

    if(resp.status === 401 || resp.status === 403) {
        dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.ERROR)) 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()
    
    dispatch(setFetchApplicationRequestStatus(REQUEST_STATUS.SUCCESS))

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0)
    {
        return respJson.businessLicenceList.filter(application => 
            ((application.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION 
                || application.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION 
                || application.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL
                || application.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_RENEWAL) 
                && (application.businessApplicationId === businessApplicationId)))
    }

    return []
}
export const fetchSearchResponse = (params, fromArchive = false) => async (dispatch, getState) => {
    dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.IN_PROGRESS))

    let mainUrl = FETCH_APPLICATIONS_URL
    
    if(fromArchive) {
        mainUrl = FETCH_ARCHIVE_APPLICATIONS_URL
    }

    //NOTE: as per backend, sending licenceNumber instead of keyowrd for licence search
    let searchAttributeName = "keyword=";
    const licenceNumberCheck = params?.licenceTypeText?.split("-")?.reverse();
    if(licenceNumberCheck?.length && !isNaN(licenceNumberCheck[0])) {
        searchAttributeName = "licenceNumber=";
    }

    //To search application on localhost with licenceNumber - used for licence transfer on BL Admin side
    if(window.location.hostname === "localhost" && searchAttributeName === "licenceNumber=") {
        mainUrl = mainUrl + "WithLicenceNumber"
    }

    let url = mainUrl;
    
    if(params?.licenceTypeText) {
        url += `?${searchAttributeName}${params.licenceTypeText}&page=${params.page}&numberOfRecords=${params.numberOfRecords}`;
        if(params.licenceTypeOption && params.licenceTypeOption !== LICENCE_SUB_TYPE_ALL) {
            url += `&licenceSubType=${params.licenceTypeOption}`
        }
    }
    else if(params.licenceTypeOption && params.licenceTypeOption !== LICENCE_SUB_TYPE_ALL){
        url += `?licenceSubType=${params.licenceTypeOption}&page=${params.page}&numberOfRecords=${params.numberOfRecords}`;
    }

    if(params?.sortType) {
        url +=  `&sortType=${params.sortType}`;
    }

    const resp = await fetch(url);

    if(resp.status === 401 || resp.status === 403) {
        dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.ERROR)) 
        return null
    }

    const data = await resp.json();
    dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.SUCCESS))
    return {...data, businessLicenceList : processQueueItemResponse(data.businessLicenceList || [])}
}

export const fetchQueue = (filters = {}) => async (dispatch, getState) => {
    if(getState().bltComponents.bltHome.fetchQueueRequestStatus === REQUEST_STATUS.IN_PROGRESS) { 
        return Promise.reject(ERROR_CODE.REQUEST_OUTSTANDING)
    }

    dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.IN_PROGRESS))

    const {page, numberOfRecords, sortType} = filters
    const filterQueryStr = "" + 
        (`page=${page ?? 1}`) +
        (`&numberOfRecords=${numberOfRecords ?? 5}`) + 
        (sortType ? `&sortType=${sortType}` : '')

    const fetchUrl = window.location.hostname === "localhost" ? FETCH_ADMIN_QUEUE_URL_LOCAL_TESTING : FETCH_ADMIN_QUEUE
    const resp = await fetch(`${fetchUrl}?${filterQueryStr}`)

    if(resp.status === 401 || resp.status === 403) {
        dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.ERROR)) 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const data = await resp.json()
    const processedData = processQueueItemResponse(data.businessLicenceList)
    dispatch(setBusinessLicenceQueue({processedData, paginationData: data.paginationData}))
    return dispatch(setFetchQueueRequestStatus(REQUEST_STATUS.SUCCESS))
}

export const fetchPrincipalBrokerRenewalWithBusinessApplicationId = (businessApplicationId) => async(dispatch, getState) => {
    /*
    fetch("licencingSubType=PRINCIPAL_BROKER_NEW_APPLICATION&businessApplicationId=15037&page=1&numberOfRecords=20&sortType=SUBMITTED_DATE_DESC")
    */
    const appUrl = FETCH_APPLICATIONS_URL
    const urlWithSearchParams = `${appUrl}?licencingSubType=PRINCIPAL_BROKER_RENEWAL&businessApplicationId=${businessApplicationId}&page=1&numberOfRecords=1`

    return fetch(urlWithSearchParams)
            .then(resp => resp.json())
            .then(data => {
                const {businessLicenceList} = data

                if(businessLicenceList
                    && Array.isArray(businessLicenceList)
                    && businessLicenceList.length > 0)
                {
                    return businessLicenceList[0]
                } else {
                    return Promise.reject(ERROR_CODE.EMPTY_RESPONSE)
                }
            })
            .catch(err => {
                return Promise.reject(err)
            })
}

export const fetchPrincipalBrokerWithBusinessApplicationId = (businessApplicationId) => async(dispatch, getState) => {
    /*
    fetch("licencingSubType=PRINCIPAL_BROKER_NEW_APPLICATION&businessApplicationId=15037&page=1&numberOfRecords=20&sortType=SUBMITTED_DATE_DESC")
    */
    const appUrl = FETCH_APPLICATIONS_URL
    const urlWithSearchParams = `${appUrl}?licencingSubType=PRINCIPAL_BROKER_NEW_APPLICATION&businessApplicationId=${businessApplicationId}&page=1&numberOfRecords=1`

    return fetch(urlWithSearchParams)
            .then(resp => resp.json())
            .then(data => {
                const {businessLicenceList} = data

                if(businessLicenceList
                    && Array.isArray(businessLicenceList)
                    && businessLicenceList.length > 0)
                {
                    return businessLicenceList[0]
                } else {
                    return Promise.reject(ERROR_CODE.EMPTY_RESPONSE)
                }
            })
            .catch(err => {
                return Promise.reject(err)
            })
}

const fetchAndMarkAssociatedApplicationStatus = (url, targetStatus, businessApplicationId) => async(dispatch, getState) => {
    const resp = await fetch(url)

    if(resp.status === 401 || resp.status === 403) { 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0) { 
            const relatedAgentApplications = respJson.businessLicenceList.filter(a => {
                
                if(a.applicationStatus !== APPLICATION_STATUS_PENDING_AGENCY){ 
                    return false
                }
                
                if(a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_MORTGAGE_BROKER_NEW_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION
                    )
                {
                    return (a.businessApplicationId === businessApplicationId)
                }
        
                return false
            })

            if(relatedAgentApplications.length) { 
                // set application status to "PENDING" for all these applications and send it to server
                const payload = relatedAgentApplications.map(a => ({...a, ...{
                    applicationStatus : targetStatus,
                    changeType : "SAVE"
                }}))
        
                const {userAuth} = getState()
                const {csrfToken} = userAuth
        
                let headers = new Headers()
                headers.append('Content-Type',"application/json")
                headers.append("X-CSRF-TOKEN", csrfToken)
        
                return fetch(SAVE_APPLICATION_STATUS_URL, {
                        method : 'POST',
                        headers: headers,
                        body : JSON.stringify(payload)
                    })
                    .then(resp => resp.json())
                    .then(data => {
                        return data
                    })
                    .catch(err => console.error(err))
            }
    }

    return Promise.resolve([])
}

export const sendBrokerRenewalApplicationStatus = (targetStatus, businessApplicationId, includingPrincipalBroker) => async(dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!businessApplicationId)
        businessApplicationId = primaryAgencyApplication.application.id

    const appUrl = FETCH_APPLICATIONS_URL
    const licenceSubTypesRequest = [
        LICENCE_SUB_TYPE_MORTGAGE_BROKER_RENEWAL_APPLICATION,
        LICENCE_SUB_TYPE_ASSOCIATE_MORTGAGE_BROKER_RENEWAL_APPLICATION
    ];
    
    if(includingPrincipalBroker) {
        licenceSubTypesRequest.push(LICENCE_SUB_TYPE_PRINCIPAL_BROKER_RENEWAL_APPLICATION)
    }

    const urlWithSearchParams = `${appUrl}?licencingSubType=${licenceSubTypesRequest.join(',')}&businessApplicationId=${businessApplicationId}`
    return dispatch(fetchAndMarkAssociatedApplicationStatus(urlWithSearchParams, targetStatus, businessApplicationId))
        .then(data => {
            return data
        })
}

export const sendBrokerApplicationStatus = (targetStatus, businessApplicationId) => async(dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!businessApplicationId)
        businessApplicationId = primaryAgencyApplication.application.id

    const appUrl = FETCH_APPLICATIONS_URL
    const urlWithSearchParams = `${appUrl}?licencingSubType=MORTGAGE_BROKER_NEW_APPLICATION,ASSOCIATE_MORTGAGE_BROKER_NEW_APPLICATION&businessApplicationId=${businessApplicationId}`
    return dispatch(fetchAndMarkAssociatedApplicationStatus(urlWithSearchParams, targetStatus, businessApplicationId))
        .then(data => {
            return data
        })
}

export const sendRenewedIndividualApplicationStatus = (
    targetStatus, 
    businessApplicationId) => async(dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!businessApplicationId)
        businessApplicationId = primaryAgencyApplication.application.id
    
    const resp = await fetch(FETCH_APPLICATIONS_URL + "?businessApplicationId=" + businessApplicationId)

    if(resp.status === 401 || resp.status === 403) { 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0
    ) { 
        const targetApplications = respJson.businessLicenceList.filter(a => {
                
            if(a.applicationStatus !== APPLICATION_STATUS_PENDING_AGENCY){ 
                return false
            }
            
            // agent or branch application with licencingSubType as "RENEWAL"
            if(a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_RENEWAL
                || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_RENEWAL
                || a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_AGENCY_RENEWAL
                || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_RENEWAL
            ) {
                return (a.businessApplicationId === businessApplicationId)
            }
    
            return false
        })

        if(targetApplications.length) { 
            // set application status to "PENDING" for all these applications and send it to server
            const payload = targetApplications.map(a => ({...a, ...{
                applicationStatus : targetStatus,
                changeType : "SAVE"
            }}))
    
            const {userAuth} = getState()
            const {csrfToken} = userAuth
    
            let headers = new Headers()
            headers.append('Content-Type',"application/json")
            headers.append("X-CSRF-TOKEN", csrfToken)
    
            return fetch(SAVE_APPLICATION_STATUS_URL, {
                    method : 'POST',
                    headers: headers,
                    body : JSON.stringify(payload)
                })
                .then(resp => resp.json())
                .then(data => {})
                .catch(err => console.error(err))
        }
    }
    
    return Promise.resolve([])
}

export const sendNewIndividualApplicationStatus = (
    targetBranchStatus,
    targetAgentStatus, 
    businessApplicationId) => async(dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!businessApplicationId)
        businessApplicationId = primaryAgencyApplication.application.id
    
    const resp = await fetch(FETCH_APPLICATIONS_URL + "?businessApplicationId=" + businessApplicationId)

    if(resp.status === 401 || resp.status === 403) { 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0
    ) { 
        const targetApplications = respJson.businessLicenceList.filter(a => {
                
            if(a.applicationStatus !== APPLICATION_STATUS_PENDING_AGENCY){ 
                return false
            }
            
            // agent or branch application with licencingSubType as "NEW"
            if(a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION
                || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION
                || a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_AGENCY_APPLICATION
                || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_APPLICATION
            ) {
                return (a.businessApplicationId === businessApplicationId)
            }
    
            return false
        })

        if(targetApplications.length) { 
            // set application status to "PENDING" for all these applications and send it to server
            const payload = targetApplications.map(a => {
                let status = null
                
                if(isIndividualTypeApplication(a)) {
                    status = targetAgentStatus
                } else if (isAgencyTypeApplication(a)) {
                    status = targetBranchStatus
                }

                if(status) {
                    return {
                        ...a,
                        applicationStatus : status, 
                        changeType : "SAVE"
                    }
                }

                return a
            })
    
            const {userAuth} = getState()
            const {csrfToken} = userAuth
    
            let headers = new Headers()
            headers.append('Content-Type',"application/json")
            headers.append("X-CSRF-TOKEN", csrfToken)
    
            return fetch(SAVE_APPLICATION_STATUS_URL, {
                    method : 'POST',
                    headers: headers,
                    body : JSON.stringify(payload)
                })
                .then(resp => resp.json())
                .then(data => {})
                .catch(err => console.error(err))
        }
    }
    
    return Promise.resolve([])
}

export const sendBranchAndCollectorApplicationStatus = (
    targetBranchStatus, 
    targetAgentStatus, 
    businessApplicationId) => async (dispatch, getState) => {
        const {primaryAgencyApplication} = getState()

        if(!businessApplicationId)
            businessApplicationId = primaryAgencyApplication.application.id
    
        const resp = await fetch(FETCH_APPLICATIONS_URL + "?businessApplicationId=" + businessApplicationId)
    
        if(resp.status === 401 || resp.status === 403) { 
            return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
        }
    
        const respJson = await resp.json()
    
        if(respJson.businessLicenceList 
            && Array.isArray(respJson.businessLicenceList)
            && respJson.businessLicenceList.length > 0) { 
                
                const relatedApplications = respJson.businessLicenceList.filter(a => {
                    return (a.businessApplicationId === businessApplicationId)
                })
    
                if(relatedApplications.length) { 
                    // set application status to "PENDING" for all these applications and send it to server
                    const payload = relatedApplications.map(a => {
                        let status = null
                        
                        if(isIndividualTypeApplication(a)) {
                            status = targetAgentStatus
                        } else if (isAgencyTypeApplication(a)) {
                            // MRA: 482 - The branch applications are following the same flow
                            // as agent applications after branches are treated separately.
                            status = targetAgentStatus
                        }

                        if(status) {
                            return {
                                ...a,
                                applicationStatus : status, 
                                changeType : "SAVE"
                            }
                        }

                        return a
                    })
            
                    const {userAuth} = getState()
                    const {csrfToken} = userAuth
            
                    let headers = new Headers()
                    headers.append('Content-Type',"application/json")
                    headers.append("X-CSRF-TOKEN", csrfToken)
            
                    return fetch(SAVE_APPLICATION_STATUS_URL, {
                            method : 'POST',
                            headers: headers,
                            body : JSON.stringify(payload)
                        })
                        .then(resp => resp.json())
                        .then(data => {})
                        .catch(err => console.error(err))
                }
        }
    
        return Promise.resolve([])
}

// need to fetch all agent applications and mark it with the targetStatus
export const sendCollectorApplicationStatus = (targetStatus, businessApplicationId) => async (dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!businessApplicationId)
        businessApplicationId = primaryAgencyApplication.application.id

    const resp = await fetch(FETCH_APPLICATIONS_URL + "?businessApplicationId=" + businessApplicationId)

    if(resp.status === 401 || resp.status === 403) { 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0) { 
            const relatedAgentApplications = respJson.businessLicenceList.filter(a => {
                
                // MRA - 1249 - Need to set status of all individual applications
                // to DENIED,CANCELLED, CANCELLED_BY_CLIENT, etc.
                // 
                // if(a.applicationStatus !== APPLICATION_STATUS_PENDING_AGENCY){ 
                //     return false
                // }
                
                if(a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_APPLICATION
                    || a.licencingSubType === LICENCE_SUB_TYPE_COLLECTION_COLLECTOR_RENEWAL
                    || a.licencingSubType === LICENCE_SUB_TYPE_DEBT_MANAGEMENT_INDIVIDUAL_RENEWAL) 
                {
                    return (a.businessApplicationId === businessApplicationId)
                }
        
                return false
            })

            if(relatedAgentApplications.length) { 
                // set application status to "PENDING" for all these applications and send it to server
                const payload = relatedAgentApplications.map(a => ({...a, ...{
                    applicationStatus : targetStatus,
                    changeType : "SAVE"
                }}))
        
                const {userAuth} = getState()
                const {csrfToken} = userAuth
        
                let headers = new Headers()
                headers.append('Content-Type',"application/json")
                headers.append("X-CSRF-TOKEN", csrfToken)
        
                return fetch(SAVE_APPLICATION_STATUS_URL, {
                        method : 'POST',
                        headers: headers,
                        body : JSON.stringify(payload)
                    })
                    .then(resp => resp.json())
                    .then(data => {})
                    .catch(err => console.error(err))
            }
    }

    return Promise.resolve([])
}

// This function calls a special API /application/status which will only accept the root level changes.
// It will ignore that fact that the businessApplicationData is null. 
export const setAllAgentLicenceStatus = (data, busineApplicationId) => async (dispatch, getState) => {
    const {primaryAgencyApplication} = getState()

    if(!busineApplicationId)
        busineApplicationId = primaryAgencyApplication.application.id

    const resp = await fetch(FETCH_APPLICATIONS_URL + "?businessApplicationId=" + busineApplicationId)

    if(resp.status === 401 || resp.status === 403) { 
        return Promise.reject(ERROR_CODE.UNAUTHORISED_ACCESS)
    }

    const respJson = await resp.json()

    if(respJson.businessLicenceList 
        && Array.isArray(respJson.businessLicenceList)
        && respJson.businessLicenceList.length > 0) { 
            const relatedAgentApplications = respJson.businessLicenceList.filter(a => {
                
                if(isIndividualTypeApplication(a)) {
                    return (a.businessApplicationId === busineApplicationId)
                }
        
                return false
            })

            if(relatedAgentApplications.length) { 
                // set application status to "PENDING" for all these applications and send it to server
                const payload = relatedAgentApplications.map(a => (
                    {
                        ...a,
                        ...data, 
                        changeType : "SAVE"
                    })
                )
        
                const {userAuth} = getState()
                const {csrfToken} = userAuth
        
                let headers = new Headers()
                headers.append('Content-Type',"application/json")
                headers.append("X-CSRF-TOKEN", csrfToken)
        
                return fetch(SAVE_LICENCE_STATUS_URL, {
                        method : 'POST',
                        headers: headers,
                        body : JSON.stringify(payload)
                    })
                    .then(resp => resp.json())
                    .then(data => {})
                    .catch(err => console.error(err))
            }
    }

    return Promise.resolve([])
}

export default bltHomeSlice.reducer
